import CryptoJS from 'crypto-js';

// Get encryption key from environment variables - NEVER hardcode in production
const ENCRYPTION_KEY = import.meta.env.VITE_ENCRYPTION_KEY || (() => {
  throw new Error('VITE_ENCRYPTION_KEY environment variable is not set. This is required for secure storage.');
})();

const SESSION_TIMEOUT_KEY = 'patient_session_timeout';
const PATIENT_TOKEN_KEY = 'patient_auth_token';

class PatientStorage {
  private isSessionValid(): boolean {
    const timeout = sessionStorage.getItem(SESSION_TIMEOUT_KEY);
    if (!timeout) return false;

    const timeoutDate = new Date(parseInt(timeout));
    const now = new Date();

    if (now > timeoutDate) {
      this.clearSession();
      return false;
    }

    return true;
  }

  private updateSessionTimeout(): void {
    const timeoutMinutes = parseInt(import.meta.env.VITE_SESSION_TIMEOUT || '60');
    const timeout = new Date();
    timeout.setMinutes(timeout.getMinutes() + timeoutMinutes);
    sessionStorage.setItem(SESSION_TIMEOUT_KEY, timeout.getTime().toString());
  }

  private encrypt(data: string): string {
    return CryptoJS.AES.encrypt(data, ENCRYPTION_KEY).toString();
  }

  private decrypt(encryptedData: string): string {
    const bytes = CryptoJS.AES.decrypt(encryptedData, ENCRYPTION_KEY);
    return bytes.toString(CryptoJS.enc.Utf8);
  }

  setItem(key: string, value: string, encrypt = true): void {
    const dataToStore = encrypt ? this.encrypt(value) : value;
    sessionStorage.setItem(key, dataToStore);
    this.updateSessionTimeout();
  }

  getItem(key: string, encrypt = true): string | null {
    if (!this.isSessionValid()) return null;

    const storedData = sessionStorage.getItem(key);
    if (!storedData) return null;

    this.updateSessionTimeout();
    return encrypt ? this.decrypt(storedData) : storedData;
  }

  removeItem(key: string): void {
    sessionStorage.removeItem(key);
  }

  clearSession(): void {
    sessionStorage.removeItem(PATIENT_TOKEN_KEY);
    sessionStorage.removeItem(SESSION_TIMEOUT_KEY);
  }

  setAuthToken(token: string): void {
    this.setItem(PATIENT_TOKEN_KEY, token, true);
  }

  getAuthToken(): string | null {
    return this.getItem(PATIENT_TOKEN_KEY, true);
  }

  removeAuthToken(): void {
    this.removeItem(PATIENT_TOKEN_KEY);
    this.clearSession();
  }

  hasActiveSession(): boolean {
    return this.isSessionValid() && this.getAuthToken() !== null;
  }
}

export const patientStorage = new PatientStorage();
